"use client";

import {
  BedDouble,
  CalendarDays,
  ListChecks,
  MapPin,
  Pencil,
  Search,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useRouter } from "next/navigation";
import { useMemo, useState, type ReactNode } from "react";

import { Container } from "@/components/layout/container";
import { OverlayPanel, OverlayPanelContent } from "@/components/overlay-panel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { useHotelSearchQuery } from "@/feature/hotel/search/api/hotel-api";
import {
  buildCustomAwareHotelSearchHref,
} from "@/feature/umrah/custom/request/custom-umrah-flow";
import { useSearchServiceFlow } from "@/feature/umrah/custom/request/hooks/use-search-service-flow";
import { toHotelSearchQueryArgs } from "@/feature/hotel/search/lib/hotel-formatter-helper";
import { requestHotelFilterResetOnNextSearch } from "@/feature/hotel/search/state/extract-hotel-filter-slice";
import { cn } from "@/lib/utils";
import { baseApi } from "@/redux/api/base-api";
import { useAppDispatch } from "@/redux/hooks";
import { skipToken } from "@reduxjs/toolkit/query";
import { IDecodedHotelSearch } from "../../types/hotel-search-type";
import { HotelFilterSection } from "../filters/hotel-filter";
import HotelFilterPopover from "../filters/hotel-filter-popover";
import HotelSearchForm from "../form/hotel-search-form";

type HotelSearchRouteInfoProps = {
  searchData: IDecodedHotelSearch;
};

import {
  formatDateRange,
  formatGuests,
  formatNights,
  formatRooms,
} from "@/feature/hotel/search/lib/hotel-search-display-helpers";

type SummaryItem = {
  key: string;
  icon: LucideIcon;
  value: string;
};

export default function HotelSearchRouteInfo({
  searchData,
}: HotelSearchRouteInfoProps) {
  const router = useRouter();
  const { source, umrahCity: customUmrahCity } = useSearchServiceFlow();
  const [showModifyBar, setShowModifyBar] = useState(!searchData.isValid);
  const isValid = searchData.isValid;

  const routeLabel = useMemo(() => {
    if (!searchData?.location?.name) {
      return "Select destination";
    }

    const country = searchData.location.country_name
      ? `, ${searchData.location.country_name}`
      : "";

    return `${searchData.location.name}${country}`;
  }, [searchData]);

  const summaryItems = useMemo<SummaryItem[]>(
    () => [
      {
        key: "dates",
        icon: CalendarDays,
        value: formatDateRange(searchData),
      },
      {
        key: "guests",
        icon: Users,
        value: formatGuests(searchData),
      },
      {
        key: "rooms",
        icon: BedDouble,
        value: `${formatRooms(searchData)} · ${formatNights(searchData)}`,
      },
    ],
    [searchData],
  );

  function handleSearchClick() {
    if (!searchData) {
      setShowModifyBar(true);
      return;
    }

    router.push(
      buildCustomAwareHotelSearchHref(searchData, {
        cityKey: customUmrahCity,
        source,
      }),
      { scroll: false },
    );
  }
  const dispatch = useAppDispatch();
  const hotelQueryArgs = useMemo(
    () => (searchData.isValid ? toHotelSearchQueryArgs(searchData) : skipToken),
    [searchData],
  );
  const { isLoading, isFetching } = useHotelSearchQuery(hotelQueryArgs);

  return (
    <>
      <OverlayPanel open={showModifyBar} onOpenChange={setShowModifyBar}>
        <section className="sticky top-(--header-height) z-40 overflow-visible border-b border-border/40 bg-background shadow-md">
          <Container className="py-3">
            <div
              aria-hidden={showModifyBar}
              className={cn(
                "flex w-full flex-col gap-2.5 transition-opacity duration-200 md:flex-row md:items-center md:justify-between",
                showModifyBar && "pointer-events-none invisible opacity-0",
              )}
            >
              <div className="flex w-full min-w-0 items-stretch gap-2 md:w-auto">
                <div className="min-w-0 flex-1 md:flex-none">
                  <RouteCard
                    routeLabel={routeLabel}
                    onClick={() => setShowModifyBar(true)}
                  />
                </div>

                <Button
                  type="button"
                  onClick={handleSearchClick}
                  className=" shrink-0 md:hidden"
                >
                  <Search className="h-4 w-4" />
                  Search
                </Button>

                <Drawer>
                  <DrawerTrigger asChild>
                    <Button variant="outline" className="ml-auto lg:hidden">
                      <ListChecks className="h-4 w-4" />
                    </Button>
                  </DrawerTrigger>
                  <DrawerContent className=" flex flex-col">
                    <DrawerHeader className="text-left">
                      <DrawerTitle className="flex items-center justify-between">
                        Hotel Filter
                        <HotelFilterPopover />
                      </DrawerTitle>
                    </DrawerHeader>

                    <div className="h-[90vh] overflow-y-auto pl-4 pr-2">
                      <HotelFilterSection />
                    </div>
                  </DrawerContent>
                </Drawer>
              </div>

              <div className="flex w-full min-w-0 sm:gap-2  md:w-auto  md:items-center md:justify-end">
                {summaryItems.map((item) => (
                  <SummaryCard
                    key={item.key}
                    icon={<item.icon className="h-3.5 w-3.5 shrink-0" />}
                    value={item.value}
                    onClick={() => setShowModifyBar(true)}
                    className="flex-1 justify-start sm:flex-none"
                  />
                ))}

                {isValid ? (
                  <Button
                    type="button"
                    onClick={() => {
                      dispatch(requestHotelFilterResetOnNextSearch());
                      dispatch(baseApi.util.invalidateTags(["HOTEL_SEARCH"]));
                    }}
                    className="hidden md:flex"
                  >
                    <Search className="h-4 w-4" />
                    Search
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowModifyBar(true)}
                    className="hidden md:flex"
                  >
                    <Pencil className="h-4 w-4" />
                    Modify
                  </Button>
                )}
              </div>
            </div>
          </Container>

          <OverlayPanelContent
            className={cn(
              "top-0 bg-background shadow-[0_8px_10px_-5px_rgba(0,0,0,0.1)]",
            )}
          >
            <Container className="py-7">
              <HotelSearchForm
                searchData={searchData ?? undefined}
                setOpen={setShowModifyBar}
              />
            </Container>
          </OverlayPanelContent>
          <div className="absolute w-full">
            {(isLoading || isFetching) && (
              <div className="bar w-full">
                <div></div>
              </div>
            )}
          </div>
        </section>
      </OverlayPanel>
    </>
  );
}

function SummaryCard({
  icon,
  value,
  onClick,
  className,
}: {
  icon: ReactNode;
  value: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <Button
      variant="ghost"
      type="button"
      onClick={onClick}
      className={cn(
        "min-w-0 border border-transparent bg-transparent px-3 hover:border-border/70 hover:bg-muted/70",
        className,
      )}
    >
      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        {icon}
      </span>
      <div className="min-w-0 text-left leading-tight">
        <p className="truncate text-sm font-semibold">{value}</p>
      </div>
    </Button>
  );
}

function RouteCard({
  routeLabel,
  onClick,
  className,
}: {
  routeLabel: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <Button
      variant="ghost"
      onClick={onClick}
      className={cn(
        "h-10 w-full min-w-0 justify-start overflow-hidden rounded-xl border border-transparent bg-transparent px-3 hover:border-border/70 hover:bg-muted/70",
        className,
      )}
    >
      <Badge>Hotels</Badge>
      <span className="inline-flex min-w-0 items-center gap-1">
        <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate text-sm font-medium">{routeLabel}</span>
      </span>
    </Button>
  );
}



